import React, {useState} from 'react'
import {useParams} from 'react-router-dom'

import {useProductList} from '../util/product-list-context'
import {Box, SimpleGrid} from '@salesforce/retail-react-app/app/components/shared/ui'
import PageHeader from './page-header'
import ProductListTopPanel from './product-list-top-panel'
import {useProductSearch} from '@salesforce/commerce-sdk-react'
import {getPageDesignerRegion} from '../../../utils/utils'
import RefinementsDrawer from './refinements-drawer'
import MetaTagsComponent from '../../../components/metatags'
import {buildMetaData} from '../../../components/metatags/util/metaDataUtils'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import BottomElements from './bottom-elements'
import {Component, PageContext} from '@salesforce/commerce-sdk-react/components'
import {GridItem} from '@chakra-ui/react'
import {PDCEditoBigTile} from '../../../page-designer/layouts/pdc-edito-big-tile'
import {PDCEditoProductTile} from '../../../page-designer/assets/pdc-edito-product-tile'
import {PDCEditoBigTilesDuo} from '../../../page-designer/layouts/pdc-edito-big-tiles-duo'
import {PDCEditoStandardTilesGroup} from '../../../page-designer/layouts/pdc-edito-standard-tiles-group'
import {PDCEditoPushContent} from '../../../page-designer/assets/pdc-edito-push-content'
import ProductListClassicSubset from './product-list-classic-subset'
import {EXCLUDED_FILTERS_LIST} from '../util/product-list-context'
import {DEFAULT_SEARCH_PARAMS, SEARCH_PARAMS_LIMIT} from '../../../constants'
import {extendRefinementExclusions} from '../../../utils/plp-utils'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_assets.pdcEditoProductTile': PDCEditoProductTile,
    'commerce_assets.pdcEditoPushContent': PDCEditoPushContent,
    'commerce_layouts.pdcEditoBigTile': PDCEditoBigTile,
    'commerce_layouts.pdcEditoBigTilesDuo': PDCEditoBigTilesDuo,
    'commerce_layouts.pdcEditoStandardTilesGroup': PDCEditoStandardTilesGroup
}

/**
 * ProductListEditorial is a React functional component that renders a product list page with editorial content.
 * It utilizes various hooks and components to fetch and display product data, metadata, and editorial layouts.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 *
 * @returns {JSX.Element} The rendered product list editorial component.
 *
 * @description
 * This component is responsible for displaying a product list page that includes editorial content.
 * It fetches product data based on search parameters and category information, and constructs metadata
 * for the page. The component conditionally renders different layouts based on the presence of refinements
 * and the fetched product data.
 *
 * The component uses the following hooks and functions:
 * - `useProductList`: Custom hook to retrieve category, editorial page, search query, and metadata.
 * - `useRef`: React hook to create a mutable ref object for the tiles grid.
 * - `useState`: React hook to manage the state of context values.
 * - `useSearchParams`: Custom hook to retrieve search parameters.
 * - `useParams`: Custom hook to retrieve URL parameters.
 * - `useProductSearch`: Custom hook to fetch product search results based on search parameters.
 * - `getPageDesignerRegion`: Function to retrieve editorial page components.
 * - `buildMetaData`: Function to construct metadata for the page.
 *
 * The component also includes conditional rendering logic to display:
 * - A loading skeleton when data is being fetched.
 * - A classic subset of products if there are selected refinements.
 * - Editorial layouts with product data if no other refinements are selected.
 *
 * The component structure includes:
 * - A header section with a `PageHeader` component.
 * - A top panel with a `ProductListTopPanel` component.
 * - A main content section that conditionally renders product tiles or editorial layouts.
 * - A bottom section with `BottomElements` and a `RefinementsDrawer` for filter options.
 */
const ProductListEditorial = ({...props}) => {
    const {category, editoPage, searchQuery, metatags} = useProductList()

    // Get Edito PD page components
    const editoLayouts = getPageDesignerRegion(editoPage, 'tiles')?.components
    const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})

    // Metadata constructed in the getProps method and passed to the MetaTags component.
    const finalMetadata = buildMetaData(category, metatags, searchQuery)

    // Product search
    const [searchParams] = useSearchParams({
        ...DEFAULT_SEARCH_PARAMS,
        limit: SEARCH_PARAMS_LIMIT
    })
    const params = useParams()
    extendRefinementExclusions(params, EXCLUDED_FILTERS_LIST)
    if (params.categoryId) {
        searchParams._refine.push(`cgid=${params.categoryId}`)
    }
    const {_refine, ...restOfParams} = searchParams
    const {data: productSearchResult} = useProductSearch(
        {
            parameters: {
                ...restOfParams,
                perPricebook: true,
                allVariationProperties: true,
                allImages: true,
                expand: ['promotions', 'variations', 'prices', 'images', 'custom_properties'],
                refine: _refine
            }
        },
        {
            keepPreviousData: true
        }
    )

    // Check if there are any selected refinements other than those in EXCLUDED_FILTERS_LIST
    const hasOtherRefinements =
        (productSearchResult?.selectedRefinements &&
            Object.keys(productSearchResult.selectedRefinements).some(
                (key) => !EXCLUDED_FILTERS_LIST.includes(key)
            )) ||
        (productSearchResult?.selectedSortingOption &&
            productSearchResult.selectedSortingOption !== 'best-matches')

    return (
        <>
            {/* Header */}
            <PageHeader />

            <Box as="section">
                {/* Top PLP panel */}
                <ProductListTopPanel />
                <Box {...props}>
                    <MetaTagsComponent searchQuery={searchQuery} metaData={finalMetadata} />
                    {hasOtherRefinements ? (
                        <ProductListClassicSubset subSet={productSearchResult?.hits} />
                    ) : (
                        <SimpleGrid
                            as="ul"
                            listStyleType={'none'}
                            mx={[10, null, null, 20]}
                            spacingY={24}
                        >
                            {editoLayouts?.map((component, index) => {
                                component.productList = productSearchResult?.hits
                                component.category = category
                                component.layoutIndex = index
                                return (
                                    <GridItem as="li" key={component.id}>
                                        <PageContext.Provider value={contextValue}>
                                            <Component component={component} />
                                        </PageContext.Provider>
                                    </GridItem>
                                )
                            })}
                        </SimpleGrid>
                    )}
                </Box>
            </Box>
            {/* PLP Bottom  */}
            <BottomElements category={category} />

            {/* Drawer for filter options */}
            <RefinementsDrawer />
        </>
    )
}

ProductListEditorial.propTypes = {}

export default ProductListEditorial
